/**
 * What each style should taste like, as bands on the flavor wheels (D-FL5).
 *
 * Written by hand from the BJCP 2021 aroma and flavor sections and our own
 * `overallImpression`, and checked against what typical grists, hop
 * schedules and yeasts actually produce on the wheels, because a band read
 * from prose alone misses real beer (PLAN-FLAVOR-BUILD, the interim
 * calibration). An axis left out is "not characteristic", `[0, 1]`.
 *
 * `null` is a style with no fixed profile: a base style plus something the
 * wheels cannot see (fruit, spice, wood, a named base that varies), or one
 * the guidelines leave open (specialty IPA, experimental). A style missing
 * from the table is one nobody has written yet, and the card says which.
 *
 * Three things every table entry allows for, measured rather than assumed:
 *
 * - A grist that is mostly base malt reads grainy 4 or more and sweet 1.5 to
 *   2.7, so pale styles allow grainy up to 5 and sweet up to 3.
 * - Dark malts are potent: a few percent of chocolate or crystal 150 reads
 *   roast 3 or more, so a mild or a brown allows more roast than its prose.
 * - An English ale strain reads clean 3 to 4 with fruity esters 2 to 3; an
 *   American one clean 5; a lager clean 5 with a little sulfur.
 *
 * Things the wheels cannot see, and so no band pretends to: fruit, spice and
 * herb additions, lactose (it is a sugar with no malt character here), rye's
 * spice, oak, and diacetyl.
 */

import type { StyleFlavorBands } from '@beerwright/core';

const BASIS = 'BJCP 2021 aroma and flavor, read 2026-09-27';

/** A clean lager fermentation. */
const LAGER = { clean: [4, 5], sulfur: [0, 2] } as const;
/** A clean American ale fermentation. */
const CLEAN_ALE = { clean: [3, 5], fruity: [0, 2] } as const;
/** An English ale fermentation: some fruit, not spotless. */
const ENGLISH_ALE = { clean: [2, 4], fruity: [1, 3] } as const;
/** Banana and clove, the German weizen yeast. */
const WEIZEN = { banana: [2, 4], clove: [1, 3], fruity: [1, 3], clean: [0, 2] } as const;
/** Sour and funky, the lambic family. */
const LAMBIC = { tart: [3, 5], funk: [2, 5], fruity: [0, 3], clean: [0, 1] } as const;

export const STYLE_BANDS: Readonly<Record<string, StyleFlavorBands | null>> = {
  // 1 · Standard American Beer

  // 1A American Light Lager: almost nothing, cleanly.
  'bjcp-2021-1a': {
    hop: {},
    malt: { grainy: [2, 5], sweet: [0, 2] },
    fermentation: LAGER,
    weights: { hop: 0.15, malt: 0.35, fermentation: 0.5 },
    basis: BASIS,
  },
  // 1B American Lager: grainy, a whisper of hops, spotless.
  'bjcp-2021-1b': {
    hop: { floral: [0, 2], spice: [0, 2], herbal: [0, 2] },
    malt: { grainy: [2, 5], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.2, malt: 0.4, fermentation: 0.4 },
    basis: BASIS,
  },
  // 1C Cream Ale: corn-sweet grainy malt, clean ale or lager yeast.
  'bjcp-2021-1c': {
    hop: { floral: [0, 2], spice: [0, 2] },
    malt: { grainy: [2, 5], sweet: [0, 3] },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.2, malt: 0.4, fermentation: 0.4 },
    basis: BASIS,
  },
  // 1D American Wheat Beer: bready wheat, clean yeast, hops anywhere from none to citrusy.
  'bjcp-2021-1d': {
    hop: { citrus: [0, 3], floral: [0, 3], spice: [0, 2], herbal: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 3], sweet: [0, 3] },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.3, malt: 0.4, fermentation: 0.3 },
    basis: BASIS,
  },

  // 2 · International Lager

  // 2A International Pale Lager: grainy and clean, light hops.
  'bjcp-2021-2a': {
    hop: { floral: [0, 2], spice: [0, 2], herbal: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.25, malt: 0.4, fermentation: 0.35 },
    basis: BASIS,
  },
  // 2B International Amber Lager: light caramel and toast, clean.
  'bjcp-2021-2b': {
    hop: { floral: [0, 2], spice: [0, 2] },
    malt: { grainy: [1, 4], caramel: [1, 3], toasty: [0, 2], bready: [0, 2], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.15, malt: 0.55, fermentation: 0.3 },
    basis: BASIS,
  },
  // 2C International Dark Lager: light roast and caramel, never heavy.
  'bjcp-2021-2c': {
    hop: {},
    malt: {
      grainy: [1, 4],
      caramel: [0, 3],
      roast: [0, 2],
      chocolate: [0, 2],
      toasty: [0, 2],
      sweet: [0, 3],
    },
    fermentation: LAGER,
    weights: { hop: 0.1, malt: 0.6, fermentation: 0.3 },
    basis: BASIS,
  },

  // 3 · Czech Lager

  // 3A Czech Pale Lager: bready malt and a generous helping of Saaz.
  'bjcp-2021-3a': {
    hop: { floral: [1, 3], herbal: [1, 3], spice: [1, 3], grassy: [0, 2], earthy: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 3], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.4, malt: 0.35, fermentation: 0.25 },
    basis: BASIS,
  },
  // 3B Czech Premium Pale Lager: rich Bohemian malt against floral-spicy Saaz.
  'bjcp-2021-3b': {
    hop: { floral: [2, 4], herbal: [1, 3], spice: [1, 4], grassy: [0, 2], earthy: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 3], caramel: [0, 2], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.45, malt: 0.35, fermentation: 0.2 },
    basis: BASIS,
  },
  // 3C Czech Amber Lager: caramel and bread first, spicy hops behind.
  'bjcp-2021-3c': {
    hop: { floral: [0, 3], spice: [0, 3], herbal: [0, 2], earthy: [0, 2] },
    malt: {
      caramel: [1, 3],
      bready: [1, 4],
      toasty: [1, 3],
      grainy: [1, 4],
      darkFruit: [0, 2],
      sweet: [0, 3],
    },
    fermentation: LAGER,
    weights: { hop: 0.25, malt: 0.5, fermentation: 0.25 },
    basis: BASIS,
  },
  // 3D Czech Dark Lager: roast and chocolate over rich bready malt.
  'bjcp-2021-3d': {
    hop: { spice: [0, 3], floral: [0, 2], herbal: [0, 2] },
    malt: {
      roast: [1, 3],
      chocolate: [1, 3],
      caramel: [0, 3],
      bready: [1, 4],
      toasty: [1, 4],
      grainy: [0, 4],
      darkFruit: [0, 2],
      sweet: [0, 3],
    },
    fermentation: LAGER,
    weights: { hop: 0.2, malt: 0.55, fermentation: 0.25 },
    basis: BASIS,
  },

  // 4 · Pale Malty European Lager

  // 4A Munich Helles: malt first, hops a floral hint, spotless.
  'bjcp-2021-4a': {
    hop: { floral: [1, 2], herbal: [0, 2], spice: [0, 2] },
    malt: { grainy: [3, 5], bready: [1, 3], sweet: [1, 2] },
    fermentation: { clean: [4, 5], sulfur: [0, 2] },
    weights: { hop: 0.2, malt: 0.5, fermentation: 0.3 },
    basis: BASIS,
  },
  // 4B Festbier: rich pale malt, a touch of toast, clean.
  'bjcp-2021-4b': {
    hop: { floral: [0, 2], herbal: [0, 2], spice: [0, 2] },
    malt: { grainy: [2, 5], bready: [1, 4], toasty: [0, 3], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.15, malt: 0.55, fermentation: 0.3 },
    basis: BASIS,
  },
  // 4C Helles Bock: bready and toasty, a little sweet, strong but clean.
  'bjcp-2021-4c': {
    hop: { floral: [0, 2], spice: [0, 2], herbal: [0, 2] },
    malt: { grainy: [1, 4], bready: [2, 4], toasty: [1, 3], sweet: [1, 3] },
    fermentation: LAGER,
    weights: { hop: 0.15, malt: 0.6, fermentation: 0.25 },
    basis: BASIS,
  },

  // 5 · Pale Bitter European Beer

  // 5A German Leichtbier: a light Pils, hops still in view.
  'bjcp-2021-5a': {
    hop: { floral: [1, 3], herbal: [1, 3], spice: [1, 3] },
    malt: { grainy: [2, 5], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.4, malt: 0.3, fermentation: 0.3 },
    basis: BASIS,
  },
  // 5B Kölsch: soft grainy malt, a whisper of fruit, lager-clean.
  'bjcp-2021-5b': {
    hop: { floral: [0, 3], herbal: [0, 2], spice: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 3] },
    fermentation: { clean: [3, 5], fruity: [0, 2], sulfur: [0, 1] },
    weights: { hop: 0.25, malt: 0.35, fermentation: 0.4 },
    basis: BASIS,
  },
  // 5C German Helles Exportbier: between Helles and Pils, balanced.
  'bjcp-2021-5c': {
    hop: { floral: [1, 3], herbal: [0, 2], spice: [0, 3] },
    malt: { grainy: [2, 5], bready: [1, 3], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.3, malt: 0.4, fermentation: 0.3 },
    basis: BASIS,
  },
  // 5D German Pils: floral-spicy noble hops over cracker malt, crisp and clean.
  'bjcp-2021-5d': {
    hop: { floral: [2, 4], herbal: [1, 3], spice: [1, 3], grassy: [0, 2], earthy: [0, 2] },
    malt: { grainy: [3, 5], bready: [0, 2], sweet: [0, 2] },
    fermentation: { clean: [4, 5], sulfur: [0, 2] },
    weights: { hop: 0.45, malt: 0.3, fermentation: 0.25 },
    basis: BASIS,
  },

  // 6 · Amber Malty European Lager

  // 6A Märzen: Munich bread crust and toast, no caramel sweetness, dry.
  'bjcp-2021-6a': {
    hop: { floral: [0, 2], spice: [0, 2], herbal: [0, 2] },
    malt: { bready: [2, 5], toasty: [2, 5], grainy: [1, 4], caramel: [0, 2], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.1, malt: 0.6, fermentation: 0.3 },
    basis: BASIS,
  },
  // 6B Rauchbier: beechwood smoke over a Märzen.
  'bjcp-2021-6b': {
    hop: {},
    malt: {
      smoke: [3, 5],
      bready: [1, 5],
      toasty: [1, 5],
      grainy: [0, 4],
      caramel: [0, 2],
      sweet: [0, 3],
    },
    fermentation: LAGER,
    weights: { hop: 0.1, malt: 0.65, fermentation: 0.25 },
    basis: BASIS,
  },
  // 6C Dunkles Bock: rich Munich toast and a little caramel, barely any hops.
  'bjcp-2021-6c': {
    hop: {},
    malt: {
      bready: [2, 5],
      toasty: [2, 5],
      sweet: [1, 3],
      caramel: [0, 3],
      darkFruit: [0, 2],
      chocolate: [0, 2],
      grainy: [0, 4],
    },
    fermentation: LAGER,
    weights: { hop: 0.05, malt: 0.7, fermentation: 0.25 },
    basis: BASIS,
  },

  // 7 · Amber Bitter European Beer

  // 7A Vienna Lager: soft toast, elegant, lightly hopped.
  'bjcp-2021-7a': {
    hop: { floral: [0, 2], spice: [0, 2], herbal: [0, 2] },
    malt: { bready: [1, 4], toasty: [1, 4], grainy: [1, 4], caramel: [0, 2], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.2, malt: 0.5, fermentation: 0.3 },
    basis: BASIS,
  },
  // 7B Altbier: bready, nutty malt with firm spicy bitterness, cold-conditioned.
  'bjcp-2021-7b': {
    hop: { spice: [1, 3], floral: [0, 2], herbal: [0, 3], earthy: [0, 2] },
    malt: {
      bready: [1, 4],
      toasty: [1, 3],
      grainy: [1, 4],
      caramel: [0, 2],
      nutty: [0, 2],
      sweet: [0, 3],
    },
    fermentation: { clean: [3, 5], fruity: [0, 2], sulfur: [0, 1] },
    weights: { hop: 0.3, malt: 0.45, fermentation: 0.25 },
    basis: BASIS,
  },
  // 7C Kellerbier: an unfiltered Helles or Pils, hops a little fresher.
  'bjcp-2021-7c': {
    hop: { floral: [1, 3], herbal: [0, 3], spice: [0, 3], grassy: [0, 2] },
    malt: { grainy: [2, 5], bready: [1, 3], sweet: [0, 3] },
    fermentation: { clean: [3, 5], sulfur: [0, 2], fruity: [0, 1] },
    weights: { hop: 0.3, malt: 0.4, fermentation: 0.3 },
    basis: BASIS,
  },

  // 8 · Dark European Lager

  // 8A Munich Dunkel: bread crust and toast, a hint of chocolate, never roasty.
  'bjcp-2021-8a': {
    hop: { spice: [0, 2], herbal: [0, 2] },
    malt: {
      bready: [3, 5],
      toasty: [2, 5],
      chocolate: [0, 2],
      roast: [0, 2],
      caramel: [0, 2],
      grainy: [0, 4],
      sweet: [0, 3],
    },
    fermentation: LAGER,
    weights: { hop: 0.1, malt: 0.65, fermentation: 0.25 },
    basis: BASIS,
  },
  // 8B Schwarzbier: dark and roasty but light on its feet, clean.
  'bjcp-2021-8b': {
    hop: { floral: [0, 2], spice: [0, 2], herbal: [0, 2] },
    malt: {
      roast: [1, 4],
      chocolate: [1, 3],
      bready: [1, 4],
      toasty: [1, 4],
      grainy: [1, 4],
      sweet: [0, 3],
    },
    fermentation: LAGER,
    weights: { hop: 0.2, malt: 0.55, fermentation: 0.25 },
    basis: BASIS,
  },

  // 9 · Strong European Beer

  // 9A Doppelbock: big bready, toasty malt, sweet, dark fruit in the dark ones.
  'bjcp-2021-9a': {
    hop: {},
    malt: {
      bready: [2, 5],
      toasty: [2, 5],
      sweet: [1, 3],
      caramel: [0, 3],
      darkFruit: [0, 3],
      chocolate: [0, 2],
      grainy: [0, 4],
    },
    fermentation: LAGER,
    weights: { hop: 0.05, malt: 0.7, fermentation: 0.25 },
    basis: BASIS,
  },
  // 9B Eisbock: concentrated doppelbock, more dark fruit and sweetness.
  'bjcp-2021-9b': {
    hop: {},
    malt: {
      bready: [2, 5],
      toasty: [2, 5],
      sweet: [1, 4],
      caramel: [0, 3],
      darkFruit: [1, 4],
      chocolate: [0, 2],
      grainy: [0, 4],
    },
    fermentation: { clean: [3, 5], fruity: [0, 2], sulfur: [0, 1] },
    weights: { hop: 0.05, malt: 0.7, fermentation: 0.25 },
    basis: BASIS,
  },
  // 9C Baltic Porter: chocolate, dark fruit and caramel, lager-smooth.
  'bjcp-2021-9c': {
    hop: { earthy: [0, 2], spice: [0, 2] },
    malt: {
      chocolate: [1, 4],
      roast: [1, 3],
      caramel: [0, 3],
      darkFruit: [1, 4],
      bready: [1, 4],
      toasty: [1, 4],
      sweet: [1, 3],
      grainy: [0, 4],
    },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.1, malt: 0.65, fermentation: 0.25 },
    basis: BASIS,
  },

  // 10 · German Wheat Beer

  // 10A Weissbier: banana and clove, bready wheat, hops barely there.
  'bjcp-2021-10a': {
    hop: {},
    malt: { grainy: [2, 5], bready: [1, 3], sweet: [0, 2] },
    fermentation: { banana: [2, 4], clove: [1, 3], fruity: [1, 3], clean: [0, 2] },
    weights: { hop: 0.1, malt: 0.3, fermentation: 0.6 },
    basis: BASIS,
  },
  // 10B Dunkles Weissbier: weizen yeast over bready, toasty dark wheat.
  'bjcp-2021-10b': {
    hop: {},
    malt: {
      bready: [1, 4],
      toasty: [1, 4],
      grainy: [1, 4],
      caramel: [0, 2],
      chocolate: [0, 2],
      darkFruit: [0, 2],
      sweet: [0, 3],
    },
    fermentation: WEIZEN,
    weights: { hop: 0.05, malt: 0.4, fermentation: 0.55 },
    basis: BASIS,
  },
  // 10C Weizenbock: a strong dunkelweizen, more dark fruit.
  'bjcp-2021-10c': {
    hop: {},
    malt: {
      bready: [1, 4],
      toasty: [1, 4],
      grainy: [1, 4],
      darkFruit: [0, 3],
      caramel: [0, 3],
      sweet: [1, 3],
    },
    fermentation: { banana: [2, 4], clove: [1, 3], fruity: [1, 4], clean: [0, 2] },
    weights: { hop: 0.05, malt: 0.4, fermentation: 0.55 },
    basis: BASIS,
  },

  // 11 · British Bitter

  // 11A Ordinary Bitter: earthy English hops, a little crystal, fruity yeast.
  'bjcp-2021-11a': {
    hop: { earthy: [1, 4], floral: [1, 3], herbal: [0, 3], spice: [0, 2], citrus: [0, 2] },
    malt: {
      grainy: [2, 5],
      caramel: [1, 3],
      bready: [0, 3],
      toasty: [0, 2],
      nutty: [0, 2],
      sweet: [0, 3],
    },
    fermentation: ENGLISH_ALE,
    weights: { hop: 0.35, malt: 0.4, fermentation: 0.25 },
    basis: BASIS,
  },
  // 11B Best Bitter: the same, a step fuller.
  'bjcp-2021-11b': {
    hop: { earthy: [1, 4], floral: [1, 3], herbal: [0, 3], spice: [0, 2], citrus: [0, 2] },
    malt: {
      grainy: [2, 5],
      caramel: [1, 3],
      bready: [0, 3],
      toasty: [0, 3],
      nutty: [0, 2],
      sweet: [0, 3],
    },
    fermentation: ENGLISH_ALE,
    weights: { hop: 0.35, malt: 0.4, fermentation: 0.25 },
    basis: BASIS,
  },
  // 11C Strong Bitter: more caramel and a hint of dark fruit.
  'bjcp-2021-11c': {
    hop: { earthy: [1, 4], floral: [1, 4], herbal: [0, 3], spice: [0, 3], citrus: [0, 3] },
    malt: {
      grainy: [2, 5],
      caramel: [1, 4],
      darkFruit: [0, 2],
      bready: [0, 3],
      toasty: [0, 3],
      nutty: [0, 2],
      sweet: [0, 3],
    },
    fermentation: ENGLISH_ALE,
    weights: { hop: 0.35, malt: 0.4, fermentation: 0.25 },
    basis: BASIS,
  },

  // 12 · Pale Commonwealth Beer

  // 12A British Golden Ale: pale and hop-led, citrus or English character.
  'bjcp-2021-12a': {
    hop: {
      floral: [1, 3],
      citrus: [0, 3],
      earthy: [0, 3],
      tropical: [0, 2],
      herbal: [0, 2],
      spice: [0, 2],
    },
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 3] },
    fermentation: { clean: [2, 5], fruity: [0, 3] },
    weights: { hop: 0.45, malt: 0.3, fermentation: 0.25 },
    basis: BASIS,
  },
  // 12B Australian Sparkling Ale: fruity yeast, herbal Pride of Ringwood.
  'bjcp-2021-12b': {
    hop: { herbal: [0, 3], earthy: [0, 2], spice: [0, 2], floral: [0, 2], citrus: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 3] },
    fermentation: ENGLISH_ALE,
    weights: { hop: 0.3, malt: 0.35, fermentation: 0.35 },
    basis: BASIS,
  },
  // 12C English IPA: earthy, floral English hops over bready malt.
  'bjcp-2021-12c': {
    hop: {
      earthy: [1, 4],
      floral: [1, 4],
      herbal: [0, 3],
      spice: [0, 3],
      citrus: [0, 3],
      resinPine: [0, 2],
    },
    malt: { grainy: [2, 5], bready: [0, 3], toasty: [0, 2], caramel: [0, 3], sweet: [0, 3] },
    fermentation: ENGLISH_ALE,
    weights: { hop: 0.5, malt: 0.3, fermentation: 0.2 },
    basis: BASIS,
  },

  // 13 · Brown British Beer

  // 13A Dark Mild: caramel and chocolate at session strength.
  'bjcp-2021-13a': {
    hop: { earthy: [0, 2] },
    malt: {
      chocolate: [1, 4],
      caramel: [1, 3],
      roast: [0, 4],
      darkFruit: [0, 3],
      nutty: [0, 2],
      toasty: [0, 2],
      bready: [0, 3],
      grainy: [0, 5],
      sweet: [0, 3],
    },
    fermentation: ENGLISH_ALE,
    weights: { hop: 0.1, malt: 0.65, fermentation: 0.25 },
    basis: BASIS,
  },
  // 13B British Brown Ale: caramel and nuts, a little chocolate.
  'bjcp-2021-13b': {
    hop: { earthy: [0, 2], floral: [0, 2] },
    malt: {
      caramel: [1, 4],
      nutty: [1, 3],
      toasty: [0, 3],
      chocolate: [0, 3],
      bready: [0, 3],
      roast: [0, 2],
      darkFruit: [0, 2],
      grainy: [1, 5],
      sweet: [0, 3],
    },
    fermentation: ENGLISH_ALE,
    weights: { hop: 0.1, malt: 0.65, fermentation: 0.25 },
    basis: BASIS,
  },
  // 13C English Porter: chocolate and roast, some caramel, English fruit.
  'bjcp-2021-13c': {
    hop: { earthy: [0, 3], floral: [0, 2], herbal: [0, 2] },
    malt: {
      chocolate: [2, 5],
      roast: [1, 5],
      caramel: [0, 3],
      darkFruit: [0, 3],
      nutty: [0, 2],
      toasty: [0, 3],
      bready: [0, 3],
      grainy: [1, 5],
      sweet: [0, 3],
    },
    fermentation: ENGLISH_ALE,
    weights: { hop: 0.15, malt: 0.6, fermentation: 0.25 },
    basis: BASIS,
  },

  // 14 · Scottish Ale

  // 14A Scottish Light: caramel malt, clean, hops out of the picture.
  'bjcp-2021-14a': {
    hop: {},
    malt: {
      caramel: [1, 4],
      sweet: [1, 3],
      toasty: [0, 3],
      bready: [0, 3],
      roast: [0, 2],
      darkFruit: [0, 2],
      grainy: [1, 5],
    },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.05, malt: 0.65, fermentation: 0.3 },
    basis: BASIS,
  },
  // 14B Scottish Heavy: the same shape, a little fuller.
  'bjcp-2021-14b': {
    hop: {},
    malt: {
      caramel: [1, 4],
      sweet: [1, 3],
      toasty: [0, 3],
      bready: [0, 3],
      roast: [0, 2],
      darkFruit: [0, 2],
      grainy: [1, 5],
    },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.05, malt: 0.65, fermentation: 0.3 },
    basis: BASIS,
  },
  // 14C Scottish Export: the strongest of the three, the same balance.
  'bjcp-2021-14c': {
    hop: {},
    malt: {
      caramel: [1, 4],
      sweet: [1, 3],
      toasty: [0, 3],
      bready: [0, 3],
      roast: [0, 2],
      darkFruit: [0, 2],
      grainy: [1, 5],
    },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.05, malt: 0.65, fermentation: 0.3 },
    basis: BASIS,
  },

  // 15 · Irish Beer

  // 15A Irish Red Ale: caramel and toast, a dry roasted finish.
  'bjcp-2021-15a': {
    hop: { earthy: [0, 2], floral: [0, 2] },
    malt: {
      caramel: [1, 4],
      toasty: [1, 3],
      bready: [0, 3],
      roast: [0, 2],
      grainy: [1, 5],
      sweet: [0, 3],
    },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.15, malt: 0.6, fermentation: 0.25 },
    basis: BASIS,
  },
  // 15B Irish Stout: roast first, a little chocolate, dry and clean-ish.
  'bjcp-2021-15b': {
    hop: { earthy: [0, 2], herbal: [0, 2] },
    malt: { roast: [3, 5], chocolate: [1, 3], grainy: [0, 4], bready: [0, 3], sweet: [0, 2] },
    fermentation: { clean: [2, 4], fruity: [0, 2] },
    weights: { hop: 0.15, malt: 0.65, fermentation: 0.2 },
    basis: BASIS,
  },
  // 15C Irish Extra Stout: fuller, more chocolate, still roast-led.
  'bjcp-2021-15c': {
    hop: { earthy: [0, 2], herbal: [0, 2] },
    malt: {
      roast: [3, 5],
      chocolate: [1, 4],
      caramel: [0, 2],
      darkFruit: [0, 2],
      grainy: [0, 4],
      bready: [0, 3],
      sweet: [0, 3],
    },
    fermentation: { clean: [2, 4], fruity: [0, 2] },
    weights: { hop: 0.15, malt: 0.65, fermentation: 0.2 },
    basis: BASIS,
  },

  // 16 · Dark British Beer

  // 16A Sweet Stout: roast and chocolate, sweet (lactose sits outside the wheels).
  'bjcp-2021-16a': {
    hop: {},
    malt: {
      roast: [2, 4],
      chocolate: [2, 4],
      sweet: [1, 4],
      caramel: [0, 3],
      grainy: [0, 4],
      bready: [0, 3],
    },
    fermentation: { clean: [2, 4], fruity: [0, 2] },
    weights: { hop: 0.05, malt: 0.75, fermentation: 0.2 },
    basis: BASIS,
  },
  // 16B Oatmeal Stout: roast and chocolate with a nutty, silky oat character.
  'bjcp-2021-16b': {
    hop: {},
    malt: {
      roast: [2, 4],
      chocolate: [2, 4],
      nutty: [0, 3],
      caramel: [0, 3],
      darkFruit: [0, 2],
      bready: [0, 3],
      grainy: [0, 5],
      sweet: [0, 3],
    },
    fermentation: { clean: [2, 4], fruity: [0, 3] },
    weights: { hop: 0.05, malt: 0.7, fermentation: 0.25 },
    basis: BASIS,
  },
  // 16C Tropical Stout: sweet roast and fruity esters.
  'bjcp-2021-16c': {
    hop: {},
    malt: {
      roast: [2, 4],
      chocolate: [2, 4],
      sweet: [1, 4],
      caramel: [0, 3],
      darkFruit: [0, 3],
      grainy: [0, 4],
    },
    fermentation: { fruity: [1, 4], clean: [1, 4] },
    weights: { hop: 0.05, malt: 0.6, fermentation: 0.35 },
    basis: BASIS,
  },
  // 16D Foreign Extra Stout: big roast, firm bitterness, some fruit.
  'bjcp-2021-16d': {
    hop: { earthy: [0, 2], herbal: [0, 2], spice: [0, 2] },
    malt: {
      roast: [3, 5],
      chocolate: [1, 4],
      caramel: [0, 3],
      darkFruit: [0, 3],
      grainy: [0, 4],
      bready: [0, 3],
      sweet: [0, 3],
    },
    fermentation: { clean: [2, 4], fruity: [0, 3] },
    weights: { hop: 0.1, malt: 0.65, fermentation: 0.25 },
    basis: BASIS,
  },

  // 17 · Strong British Ale

  // 17A British Strong Ale: caramel and toffee, some fruit, English hops.
  'bjcp-2021-17a': {
    hop: { earthy: [0, 3], floral: [0, 3], herbal: [0, 2] },
    malt: {
      caramel: [1, 4],
      darkFruit: [0, 3],
      toasty: [0, 3],
      bready: [0, 3],
      nutty: [0, 2],
      grainy: [1, 5],
      sweet: [1, 3],
    },
    fermentation: { clean: [1, 4], fruity: [1, 4] },
    weights: { hop: 0.2, malt: 0.5, fermentation: 0.3 },
    basis: BASIS,
  },
  // 17B Old Ale: dark fruit and caramel, aged, sometimes a little tart or funky.
  'bjcp-2021-17b': {
    hop: { earthy: [0, 2] },
    malt: {
      caramel: [1, 4],
      darkFruit: [1, 4],
      sweet: [1, 4],
      toasty: [0, 3],
      nutty: [0, 3],
      chocolate: [0, 2],
      grainy: [0, 5],
    },
    fermentation: { fruity: [1, 4], clean: [1, 3], funk: [0, 2], tart: [0, 2] },
    weights: { hop: 0.05, malt: 0.6, fermentation: 0.35 },
    basis: BASIS,
  },
  // 17C Wee Heavy: rich caramel and sweetness, hops nowhere.
  'bjcp-2021-17c': {
    hop: {},
    malt: {
      caramel: [2, 5],
      sweet: [2, 4],
      toasty: [0, 3],
      bready: [0, 3],
      darkFruit: [0, 3],
      grainy: [0, 5],
    },
    fermentation: { clean: [2, 5], fruity: [0, 3] },
    weights: { hop: 0.05, malt: 0.7, fermentation: 0.25 },
    basis: BASIS,
  },
  // 17D English Barleywine: rich malt and dark fruit, English hops for balance.
  'bjcp-2021-17d': {
    hop: { earthy: [0, 3], floral: [0, 3], herbal: [0, 2] },
    malt: {
      caramel: [1, 4],
      darkFruit: [1, 4],
      sweet: [1, 4],
      toasty: [0, 3],
      bready: [0, 3],
      nutty: [0, 2],
      grainy: [1, 5],
    },
    fermentation: { fruity: [1, 4], clean: [1, 3] },
    weights: { hop: 0.15, malt: 0.55, fermentation: 0.3 },
    basis: BASIS,
  },

  // 18 · Pale American Ale

  // 18A Blonde Ale: easy, grainy, lightly hopped, clean.
  'bjcp-2021-18a': {
    hop: { floral: [0, 2], citrus: [0, 2], spice: [0, 2], herbal: [0, 2], earthy: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 2], caramel: [0, 2], sweet: [0, 3] },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.25, malt: 0.45, fermentation: 0.3 },
    basis: BASIS,
  },
  // 18B American Pale Ale: American hops lead, citrus most often, a little malt support.
  'bjcp-2021-18b': {
    hop: {
      citrus: [2, 5],
      floral: [0, 4],
      resinPine: [0, 4],
      tropical: [0, 4],
      stoneFruit: [0, 3],
      berry: [0, 3],
      spice: [0, 2],
      herbal: [0, 2],
      earthy: [0, 2],
    },
    malt: { grainy: [2, 5], bready: [0, 3], toasty: [0, 2], caramel: [0, 3], sweet: [0, 3] },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.55, malt: 0.25, fermentation: 0.2 },
    basis: BASIS,
  },

  // 19 · Amber and Brown American Beer

  // 19A American Amber Ale: caramel malt and American hops in balance.
  'bjcp-2021-19a': {
    hop: {
      citrus: [1, 4],
      floral: [0, 3],
      resinPine: [0, 3],
      tropical: [0, 3],
      spice: [0, 2],
      herbal: [0, 2],
      earthy: [0, 2],
    },
    malt: {
      caramel: [2, 4],
      toasty: [0, 3],
      bready: [0, 3],
      darkFruit: [0, 2],
      grainy: [2, 5],
      sweet: [0, 3],
    },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.35, malt: 0.45, fermentation: 0.2 },
    basis: BASIS,
  },
  // 19B California Common: woody, minty Northern Brewer over toast and caramel.
  'bjcp-2021-19b': {
    hop: { resinPine: [1, 3], herbal: [1, 3], earthy: [0, 3], spice: [0, 2], floral: [0, 2] },
    malt: { toasty: [1, 3], caramel: [1, 3], bready: [0, 3], grainy: [1, 5], sweet: [0, 3] },
    fermentation: { clean: [3, 5], fruity: [0, 2], sulfur: [0, 1] },
    weights: { hop: 0.35, malt: 0.4, fermentation: 0.25 },
    basis: BASIS,
  },
  // 19C American Brown Ale: caramel and chocolate, American hops optional.
  'bjcp-2021-19c': {
    hop: { citrus: [0, 3], floral: [0, 2], resinPine: [0, 2], earthy: [0, 2] },
    malt: {
      caramel: [1, 3],
      chocolate: [1, 3],
      nutty: [0, 3],
      toasty: [0, 3],
      roast: [0, 2],
      darkFruit: [0, 2],
      bready: [0, 3],
      grainy: [1, 5],
      sweet: [0, 3],
    },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.2, malt: 0.6, fermentation: 0.2 },
    basis: BASIS,
  },

  // 20 · American Porter and Stout

  // 20A American Porter: chocolate and roast, American hops in support.
  'bjcp-2021-20a': {
    hop: { citrus: [0, 3], floral: [0, 2], resinPine: [0, 2], earthy: [0, 2] },
    malt: {
      chocolate: [2, 5],
      roast: [2, 5],
      caramel: [0, 3],
      darkFruit: [0, 2],
      toasty: [0, 2],
      grainy: [1, 5],
      sweet: [0, 3],
    },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.15, malt: 0.65, fermentation: 0.2 },
    basis: BASIS,
  },
  // 20B American Stout: bold roast, American hops can show.
  'bjcp-2021-20b': {
    hop: { citrus: [0, 3], resinPine: [0, 3], floral: [0, 2], earthy: [0, 2] },
    malt: {
      roast: [3, 5],
      chocolate: [2, 4],
      caramel: [0, 2],
      grainy: [0, 5],
      sweet: [0, 3],
    },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.25, malt: 0.55, fermentation: 0.2 },
    basis: BASIS,
  },
  // 20C Imperial Stout: roast, chocolate and dark fruit, big and rich.
  'bjcp-2021-20c': {
    hop: { earthy: [0, 3], citrus: [0, 2], resinPine: [0, 2], floral: [0, 2], herbal: [0, 2] },
    malt: {
      roast: [3, 5],
      chocolate: [2, 5],
      darkFruit: [0, 4],
      caramel: [0, 3],
      sweet: [1, 4],
      bready: [0, 2],
      toasty: [0, 2],
      grainy: [0, 5],
    },
    fermentation: { clean: [2, 5], fruity: [0, 3] },
    weights: { hop: 0.1, malt: 0.65, fermentation: 0.25 },
    basis: BASIS,
  },

  // 21 · IPA

  // 21A American IPA: BJCP lets any one or more New World characters lead; clean, restrained malt.
  'bjcp-2021-21a': {
    hop: {
      citrus: [2, 5],
      tropical: [0, 5],
      stoneFruit: [0, 4],
      berry: [0, 4],
      floral: [0, 4],
      resinPine: [1, 5],
      earthy: [0, 3],
      herbal: [0, 2],
      spice: [0, 2],
    },
    malt: { grainy: [2, 5], bready: [0, 3], toasty: [0, 2], caramel: [0, 2], sweet: [0, 2] },
    fermentation: { clean: [3, 5], fruity: [0, 2] },
    weights: { hop: 0.6, malt: 0.2, fermentation: 0.2 },
    basis: BASIS,
  },
  // 21B Specialty IPA: black, brown, red, rye, white and Belgian IPAs share
  // only their hops; each is judged against its own named variant.
  'bjcp-2021-21b': null,
  // 21C Hazy IPA: tropical and stone fruit, soft malt, a fruity yeast.
  'bjcp-2021-21c': {
    hop: {
      tropical: [2, 5],
      citrus: [1, 5],
      stoneFruit: [1, 5],
      berry: [0, 4],
      resinPine: [0, 4],
      floral: [0, 3],
      earthy: [0, 3],
    },
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 2] },
    fermentation: { fruity: [2, 4], clean: [1, 3] },
    weights: { hop: 0.6, malt: 0.15, fermentation: 0.25 },
    basis: BASIS,
  },

  // 22 · Strong American Ale

  // 22A Double IPA: an American IPA turned up, still dry and clean.
  'bjcp-2021-22a': {
    hop: {
      citrus: [2, 5],
      tropical: [0, 5],
      stoneFruit: [0, 5],
      berry: [0, 4],
      floral: [0, 4],
      resinPine: [1, 5],
      earthy: [0, 3],
      herbal: [0, 3],
      spice: [0, 2],
    },
    malt: { grainy: [2, 5], bready: [0, 3], toasty: [0, 2], caramel: [0, 2], sweet: [0, 3] },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.65, malt: 0.15, fermentation: 0.2 },
    basis: BASIS,
  },
  // 22B American Strong Ale: caramel malt and assertive American hops.
  'bjcp-2021-22b': {
    hop: {
      citrus: [1, 4],
      resinPine: [1, 4],
      floral: [0, 3],
      tropical: [0, 3],
      earthy: [0, 3],
      herbal: [0, 2],
    },
    malt: {
      caramel: [1, 4],
      toasty: [0, 3],
      darkFruit: [0, 3],
      bready: [0, 3],
      grainy: [1, 5],
      sweet: [0, 3],
    },
    fermentation: { clean: [2, 5], fruity: [0, 3] },
    weights: { hop: 0.4, malt: 0.4, fermentation: 0.2 },
    basis: BASIS,
  },
  // 22C American Barleywine: rich malt with bold American hops.
  'bjcp-2021-22c': {
    hop: {
      citrus: [1, 5],
      resinPine: [1, 5],
      floral: [0, 4],
      tropical: [0, 4],
      earthy: [0, 3],
      herbal: [0, 2],
    },
    malt: {
      caramel: [1, 4],
      darkFruit: [0, 3],
      toasty: [0, 3],
      bready: [0, 3],
      sweet: [1, 4],
      grainy: [1, 5],
    },
    fermentation: { clean: [2, 5], fruity: [0, 3] },
    weights: { hop: 0.4, malt: 0.4, fermentation: 0.2 },
    basis: BASIS,
  },
  // 22D Wheatwine: bready wheat, rich and warming, hops optional.
  'bjcp-2021-22d': {
    hop: { citrus: [0, 3], floral: [0, 3], resinPine: [0, 2], earthy: [0, 2] },
    malt: {
      grainy: [2, 5],
      bready: [1, 4],
      caramel: [0, 3],
      toasty: [0, 3],
      darkFruit: [0, 2],
      sweet: [1, 4],
    },
    fermentation: { clean: [2, 5], fruity: [0, 3] },
    weights: { hop: 0.25, malt: 0.5, fermentation: 0.25 },
    basis: BASIS,
  },

  // 23 · European Sour Ale

  // 23A Berliner Weisse: sharp and clean-sour, bready wheat, no hops.
  'bjcp-2021-23a': {
    hop: {},
    malt: { grainy: [2, 5], bready: [0, 3], sweet: [0, 2] },
    fermentation: { tart: [3, 5], clean: [0, 3], funk: [0, 3], fruity: [0, 2] },
    weights: { hop: 0.05, malt: 0.25, fermentation: 0.7 },
    basis: BASIS,
  },
  // 23B Flanders Red Ale: sour, fruity and caramel, like a red wine.
  'bjcp-2021-23b': {
    hop: {},
    malt: {
      caramel: [1, 3],
      darkFruit: [1, 4],
      toasty: [0, 3],
      bready: [0, 2],
      chocolate: [0, 1],
      grainy: [0, 5],
      sweet: [0, 3],
    },
    fermentation: { tart: [3, 5], fruity: [2, 4], funk: [0, 3], clean: [0, 1] },
    weights: { hop: 0.05, malt: 0.35, fermentation: 0.6 },
    basis: BASIS,
  },
  // 23C Oud Bruin: malty dark fruit and caramel, a gentler sourness.
  'bjcp-2021-23c': {
    hop: {},
    malt: {
      caramel: [1, 4],
      darkFruit: [1, 4],
      sweet: [1, 3],
      toasty: [0, 3],
      chocolate: [0, 2],
      bready: [0, 2],
      grainy: [0, 5],
    },
    fermentation: { tart: [2, 4], fruity: [1, 4], funk: [0, 2], clean: [0, 2] },
    weights: { hop: 0.05, malt: 0.45, fermentation: 0.5 },
    basis: BASIS,
  },
  // 23D Lambic: sour and funky over grainy wheat, aged hops unnoticed.
  'bjcp-2021-23d': {
    hop: {},
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 2] },
    fermentation: LAMBIC,
    weights: { hop: 0.05, malt: 0.2, fermentation: 0.75 },
    basis: BASIS,
  },
  // 23E Gueuze: a blend of lambics, the same shape, more complex.
  'bjcp-2021-23e': {
    hop: {},
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 2] },
    fermentation: LAMBIC,
    weights: { hop: 0.05, malt: 0.2, fermentation: 0.75 },
    basis: BASIS,
  },
  // 23F Fruit Lambic: a lambic under the fruit; the fruit itself is off the wheels.
  'bjcp-2021-23f': {
    hop: {},
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 2] },
    fermentation: LAMBIC,
    weights: { hop: 0.05, malt: 0.2, fermentation: 0.75 },
    basis: BASIS,
  },
  // 23G Gose: lightly sour, salty and coriander (both off the wheels), bready wheat.
  'bjcp-2021-23g': {
    hop: { herbal: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 3], sweet: [0, 2] },
    fermentation: { tart: [2, 4], clean: [0, 3], funk: [0, 2], fruity: [0, 2] },
    weights: { hop: 0.05, malt: 0.3, fermentation: 0.65 },
    basis: BASIS,
  },

  // 24 · Belgian Ale

  // 24A Witbier: citrusy, peppery yeast over soft wheat; orange and coriander are off the wheels.
  'bjcp-2021-24a': {
    hop: {},
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 3] },
    fermentation: {
      fruity: [1, 4],
      pepper: [1, 3],
      clove: [0, 3],
      tart: [0, 2],
      clean: [0, 2],
    },
    weights: { hop: 0.05, malt: 0.3, fermentation: 0.65 },
    basis: BASIS,
  },
  // 24B Belgian Pale Ale: bready, lightly toasty malt, fruity yeast, spicy hops.
  'bjcp-2021-24b': {
    hop: { floral: [0, 3], spice: [0, 3], herbal: [0, 2], earthy: [0, 2] },
    malt: {
      bready: [1, 3],
      toasty: [0, 3],
      caramel: [0, 2],
      nutty: [0, 2],
      grainy: [2, 5],
      sweet: [0, 3],
    },
    fermentation: { fruity: [1, 3], clean: [1, 3], pepper: [0, 2] },
    weights: { hop: 0.25, malt: 0.4, fermentation: 0.35 },
    basis: BASIS,
  },
  // 24C Bière de Garde: toasty, bready malt, clean or a little fruity.
  'bjcp-2021-24c': {
    hop: { earthy: [0, 2], spice: [0, 2], herbal: [0, 2] },
    malt: {
      toasty: [1, 4],
      bready: [1, 4],
      caramel: [0, 3],
      darkFruit: [0, 2],
      grainy: [1, 5],
      sweet: [0, 3],
    },
    fermentation: { clean: [2, 5], fruity: [0, 3] },
    weights: { hop: 0.1, malt: 0.6, fermentation: 0.3 },
    basis: BASIS,
  },

  // 25 · Strong Belgian Ale

  // 25A Belgian Blond Ale: soft pale malt, a touch of sweetness, fruity-spicy yeast.
  'bjcp-2021-25a': {
    hop: { spice: [0, 2], floral: [0, 2], herbal: [0, 2] },
    malt: { grainy: [2, 5], sweet: [1, 3], bready: [0, 2] },
    fermentation: { fruity: [1, 4], pepper: [0, 3], clove: [0, 2], clean: [0, 2] },
    weights: { hop: 0.1, malt: 0.3, fermentation: 0.6 },
    basis: BASIS,
  },
  // 25B Saison: pepper and fruit from the yeast, dry grainy malt, hops in support.
  'bjcp-2021-25b': {
    hop: { floral: [1, 3], herbal: [0, 2], spice: [1, 3], citrus: [0, 2], earthy: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 2] },
    fermentation: {
      pepper: [2, 4],
      fruity: [2, 4],
      clove: [0, 2],
      clean: [0, 2],
      tart: [0, 2],
      funk: [0, 2],
    },
    weights: { hop: 0.25, malt: 0.2, fermentation: 0.55 },
    basis: BASIS,
  },
  // 25C Belgian Golden Strong Ale: fruity, peppery, bone dry, spicy hops.
  'bjcp-2021-25c': {
    hop: { spice: [1, 3], floral: [0, 3], herbal: [0, 2] },
    malt: { grainy: [2, 5], sweet: [0, 3] },
    fermentation: { fruity: [2, 4], pepper: [1, 3], clove: [0, 3], clean: [0, 2] },
    weights: { hop: 0.15, malt: 0.2, fermentation: 0.65 },
    basis: BASIS,
  },

  // 26 · Monastic Ale

  // 26A Belgian Single: pale, hoppy and dry for a Trappist, fruity-spicy yeast.
  'bjcp-2021-26a': {
    hop: { floral: [1, 3], spice: [1, 3], herbal: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 3] },
    fermentation: { fruity: [1, 3], pepper: [0, 3], clove: [0, 2], clean: [0, 3] },
    weights: { hop: 0.3, malt: 0.3, fermentation: 0.4 },
    basis: BASIS,
  },
  // 26B Belgian Dubbel: caramel and dark fruit, raisins and plums, fruity yeast.
  'bjcp-2021-26b': {
    hop: {},
    malt: {
      caramel: [1, 3],
      darkFruit: [1, 4],
      toasty: [0, 3],
      bready: [0, 3],
      chocolate: [0, 1],
      grainy: [1, 5],
      sweet: [1, 3],
    },
    fermentation: { fruity: [2, 4], pepper: [0, 3], clove: [0, 2], banana: [0, 2], clean: [0, 3] },
    weights: { hop: 0.05, malt: 0.45, fermentation: 0.5 },
    basis: BASIS,
  },
  // 26C Belgian Tripel: fruity and peppery, pale malt with a touch of sweetness, spicy hops.
  'bjcp-2021-26c': {
    hop: { spice: [1, 3], floral: [1, 2], herbal: [0, 2] },
    malt: { grainy: [2, 5], sweet: [1, 2], bready: [0, 1] },
    fermentation: {
      fruity: [2, 4],
      pepper: [1, 3],
      clove: [0, 2],
      clean: [0, 2],
      banana: [0, 1],
    },
    weights: { hop: 0.15, malt: 0.25, fermentation: 0.6 },
    basis: BASIS,
  },
  // 26D Belgian Dark Strong Ale: rich dark fruit and caramel, complex yeast.
  'bjcp-2021-26d': {
    hop: {},
    malt: {
      darkFruit: [1, 5],
      caramel: [1, 4],
      sweet: [1, 4],
      toasty: [0, 3],
      bready: [0, 3],
      chocolate: [0, 2],
      grainy: [1, 5],
    },
    fermentation: { fruity: [2, 5], pepper: [0, 3], clove: [0, 4], banana: [0, 2], clean: [0, 2] },
    weights: { hop: 0.05, malt: 0.45, fermentation: 0.5 },
    basis: BASIS,
  },

  // 27 · Historical Beer

  // 27A Kentucky Common: a dark cream ale, a little caramel and roast.
  'bjcp-2021-27a': {
    hop: { floral: [0, 2], spice: [0, 2], herbal: [0, 2] },
    malt: {
      grainy: [2, 5],
      caramel: [0, 3],
      toasty: [0, 2],
      roast: [0, 2],
      chocolate: [0, 2],
      sweet: [0, 3],
    },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.15, malt: 0.55, fermentation: 0.3 },
    basis: BASIS,
  },
  // 27B Lichtenhainer: smoky and sour, light and dry.
  'bjcp-2021-27b': {
    hop: {},
    malt: { smoke: [2, 4], grainy: [2, 5], bready: [0, 2] },
    fermentation: { tart: [2, 4], clean: [0, 3], fruity: [0, 2], funk: [0, 2] },
    weights: { hop: 0.05, malt: 0.45, fermentation: 0.5 },
    basis: BASIS,
  },
  // 27C London Brown Ale: sweet caramel and toffee, low alcohol, low hops.
  'bjcp-2021-27c': {
    hop: {},
    malt: {
      caramel: [1, 4],
      sweet: [2, 4],
      chocolate: [0, 3],
      darkFruit: [0, 3],
      roast: [0, 2],
      toasty: [0, 2],
      nutty: [0, 2],
      grainy: [0, 5],
    },
    fermentation: ENGLISH_ALE,
    weights: { hop: 0.05, malt: 0.75, fermentation: 0.2 },
    basis: BASIS,
  },
  // 27D Piwo Grodziskie: oak-smoked wheat, crisp, herbal hops.
  'bjcp-2021-27d': {
    hop: { herbal: [0, 3], spice: [0, 3], floral: [0, 2], earthy: [0, 2] },
    malt: { smoke: [2, 4], grainy: [1, 4], bready: [0, 2] },
    fermentation: { clean: [2, 5], fruity: [0, 2] },
    weights: { hop: 0.2, malt: 0.5, fermentation: 0.3 },
    basis: BASIS,
  },
  // 27E Pre-Prohibition Lager: a hoppier American lager with corn and Cluster.
  'bjcp-2021-27e': {
    hop: { floral: [1, 3], spice: [0, 3], herbal: [0, 3], earthy: [0, 2], resinPine: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.35, malt: 0.35, fermentation: 0.3 },
    basis: BASIS,
  },
  // 27F Pre-Prohibition Porter: chocolate and light roast, lager-clean or nearly.
  'bjcp-2021-27f': {
    hop: { earthy: [0, 2], herbal: [0, 2], spice: [0, 2] },
    malt: {
      chocolate: [1, 4],
      roast: [1, 3],
      caramel: [0, 3],
      darkFruit: [0, 2],
      toasty: [0, 2],
      grainy: [1, 5],
      sweet: [0, 3],
    },
    fermentation: { clean: [3, 5], fruity: [0, 2], sulfur: [0, 1] },
    weights: { hop: 0.1, malt: 0.65, fermentation: 0.25 },
    basis: BASIS,
  },
  // 27G Roggenbier: a rye dunkelweizen; the rye's spice is off the wheels.
  'bjcp-2021-27g': {
    hop: {},
    malt: { grainy: [2, 5], bready: [1, 4], toasty: [0, 3], caramel: [0, 2], sweet: [0, 3] },
    fermentation: { banana: [1, 3], clove: [1, 3], fruity: [1, 3], clean: [0, 2] },
    weights: { hop: 0.05, malt: 0.45, fermentation: 0.5 },
    basis: BASIS,
  },
  // 27H Sahti: sweet, bready malt and banana-ish yeast; juniper is off the wheels.
  'bjcp-2021-27h': {
    hop: {},
    malt: {
      bready: [1, 4],
      toasty: [0, 3],
      caramel: [0, 2],
      grainy: [1, 5],
      sweet: [1, 4],
    },
    fermentation: { banana: [1, 4], fruity: [1, 4], clove: [0, 2], clean: [0, 2] },
    weights: { hop: 0.05, malt: 0.45, fermentation: 0.5 },
    basis: BASIS,
  },

  // 28–34 · Specialty categories. Each is a base style plus something the
  // wheels cannot see, or a base that varies from entry to entry, so none
  // has one fixed profile.

  'bjcp-2021-28a': null, // Brett Beer: any base style with Brettanomyces.
  'bjcp-2021-28b': null, // Mixed-Fermentation Sour Beer: any base, any culture.
  'bjcp-2021-28c': null, // Wild Specialty Beer: a sour plus fruit, spice or wood.
  'bjcp-2021-28d': null, // Straight Sour Beer: any base, kettle or pitched souring.
  'bjcp-2021-29a': null, // Fruit Beer
  'bjcp-2021-29b': null, // Fruit and Spice Beer
  'bjcp-2021-29c': null, // Specialty Fruit Beer
  'bjcp-2021-29d': null, // Grape Ale
  'bjcp-2021-30a': null, // Spice, Herb, or Vegetable Beer
  'bjcp-2021-30b': null, // Autumn Seasonal Beer
  'bjcp-2021-30c': null, // Winter Seasonal Beer
  'bjcp-2021-30d': null, // Specialty Spice Beer
  'bjcp-2021-31a': null, // Alternative Grain Beer
  'bjcp-2021-31b': null, // Alternative Sugar Beer
  'bjcp-2021-32a': null, // Classic Style Smoked Beer: smoke over a named base.
  'bjcp-2021-32b': null, // Specialty Smoked Beer
  'bjcp-2021-33a': null, // Wood-Aged Beer
  'bjcp-2021-33b': null, // Specialty Wood-Aged Beer
  'bjcp-2021-34a': null, // Clone Beer
  'bjcp-2021-34b': null, // Mixed-Style Beer
  'bjcp-2021-34c': null, // Experimental Beer

  // Provisional styles

  // X1 Dorada Pampeana: a simple, grainy golden ale.
  'bjcp-2021-x1': {
    hop: { floral: [0, 2], citrus: [0, 2], spice: [0, 2] },
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 3] },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.2, malt: 0.45, fermentation: 0.35 },
    basis: BASIS,
  },
  // X2 IPA Argenta: an IPA with wheat and Argentine citrus hops.
  'bjcp-2021-x2': {
    hop: {
      citrus: [2, 5],
      tropical: [0, 4],
      floral: [0, 3],
      resinPine: [0, 3],
      herbal: [0, 2],
    },
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 3] },
    fermentation: CLEAN_ALE,
    weights: { hop: 0.6, malt: 0.2, fermentation: 0.2 },
    basis: BASIS,
  },
  'bjcp-2021-x3': null, // Italian Grape Ale: the grape is the point, and off the wheels.
  // X4 Catharina Sour: a light, clean-sour base; the fruit is off the wheels.
  'bjcp-2021-x4': {
    hop: {},
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 2] },
    fermentation: { tart: [2, 4], clean: [0, 3], fruity: [0, 2] },
    weights: { hop: 0.05, malt: 0.3, fermentation: 0.65 },
    basis: BASIS,
  },
  // X5 New Zealand Pilsner: tropical, citrusy, grassy NZ hops over a clean lager.
  'bjcp-2021-x5': {
    hop: {
      tropical: [1, 4],
      citrus: [1, 4],
      berry: [0, 3],
      stoneFruit: [0, 3],
      floral: [0, 3],
      grassy: [0, 3],
      herbal: [0, 2],
      resinPine: [0, 2],
    },
    malt: { grainy: [2, 5], bready: [0, 2], sweet: [0, 3] },
    fermentation: LAGER,
    weights: { hop: 0.5, malt: 0.25, fermentation: 0.25 },
    basis: BASIS,
  },
};

export function styleBands(styleId: string): StyleFlavorBands | null | undefined {
  return Object.hasOwn(STYLE_BANDS, styleId) ? STYLE_BANDS[styleId] : undefined;
}
